'use client'

import { useState } from 'react'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import { useSession } from 'next-auth/react'

import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import Button from 'shared/ui/Button'
import Input from 'shared/ui/form/Input'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { removeCustomerAccount } from '../api'
import { useAccountPort } from '../ports'
import { extractTokenFromClientSession } from '../session'

import styles from './CustomerDeleteProfileForm.module.scss'

const CustomerDeleteProfileForm = () => {
  const session = useSession()
  const [open, setOpen] = useState(false)
  const token = extractTokenFromClientSession(session)
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState('')

  const { onAccountDeleted } = useAccountPort()

  const handleValueChange = (_, value: string) => {
    setPassword(value)
  }

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setPassword('')
  }

  const handleSubmit = async () => {
    setLoading(true)

    await removeCustomerAccount({ token, password })
      .then(async () => {
        syneriseSendFormData({
          formData: {},
          event: SyneriseFormEvent.DELETE_ACCOUNT
        })

        setLoading(false)
        toast.success('Pomyślnie usunięto konto')

        handleClose()
        await onAccountDeleted()
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
        toast.error(getApiErrors(err))
      })
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isNilOrEmpty(password) || loading) return

    void handleSubmit()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content} id='profile_edit_account_removal'>
        <div className={styles.infoBoxWrapper}>
          <div className={styles.iconWrapper}>{<IconTrash />}</div>
          <div className={styles.infoText}>Usunięcie konta</div>
        </div>
        <Button
          id='remove_account_delete_button'
          startIcon={<IconPencil />}
          variant='neutralOutline'
          onClick={handleOpen}
        >
          Usuń konto
        </Button>
        <Modal
          id='remove_account_modal'
          title='Potwierdź usunięcie konta'
          open={open}
          onClose={handleClose}
        >
          <ModalTitle>Potwierdź usunięcie konta</ModalTitle>
          <ModalDescription>
            Potwierdź, że chcesz usunąć bezpowrotnie swoje konto użytkownika.
            Będziesz mogła/mógł później utworzyć nowe przy użyciu tego samego
            adresu e-mail.
          </ModalDescription>
          <form
            className={styles.modalContent}
            noValidate
            onSubmit={handleFormSubmit}
          >
            <Input
              id='remove_account_password_confirmation'
              required
              label='Wpisz obecne hasło celem potwierdzenia'
              type='password'
              name='currentPassword'
              inputProps={{ autoComplete: 'current-password' }}
              onChange={handleValueChange}
              value={password}
            />
            <div className={styles.buttonsWrapper}>
              <Button
                id='remove_account_confirm_button'
                type='submit'
                fullWidth
                variant='primary'
                disabled={isNilOrEmpty(password) || loading}
              >
                Potwierdź
              </Button>
              <Button
                id='remove_account_cancel_button'
                type='button'
                fullWidth
                onClick={handleClose}
                variant='neutralOutline'
              >
                Anuluj
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  )
}

export default CustomerDeleteProfileForm
